import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useFeatures, useMixpanel, useNotify } from '../../../../hooks';
import { useActiveProjectId } from '../../../../hooks/useActiveProjectId';
import { useApiClient } from '../../../../hooks/useRestAPIHooks';
import { useExecutiveSummaryData } from '../ExecutiveSummary/Context';

/** Which part of the action-plan generation a thumb is about. */
export type ActionPlanFeedbackTarget = 'coach-narrative' | 'action-item';

/** Thumbs up/down. */
export type ActionPlanFeedbackSentiment = 'positive' | 'negative';

export type SubmitActionPlanFeedbackArgs = {
  /**
   * The coach run that produced this action plan
   * (`"zori-pulse-coach/<task_id>"`), read from the coach-artifact provenance.
   * Feedback is tied to it so the nightly guidance summarizer can attribute it
   * back to the action-plan scan category.
   */
  sourceRunId: string;
  target: ActionPlanFeedbackTarget;
  sentiment: ActionPlanFeedbackSentiment;
  /** Present for per-item feedback; omitted for coach-narrative feedback. */
  actionItemTitle?: string;
  comment?: string;
};

export type UseActionPlanFeedback = {
  /** Gate: the Zori Pulse scan feature is on for this account. */
  zoriPulseEnabled: boolean;
  isSubmitting: boolean;
  /**
   * POSTs the feedback to the agents action-plan feedback endpoint. Resolves to
   * `true` when the score was recorded (the run mapped to a coach generation),
   * `false` otherwise. Never throws — failures surface via a toast.
   */
  submitFeedback: (args: SubmitActionPlanFeedbackArgs) => Promise<boolean>;
};

const messages = defineMessages({
  submitted: {
    id: 'actionPlan.feedback.submitted',
    defaultMessage:
      'Thanks — your feedback will help tune the next action plan.',
  },
  failed: {
    id: 'actionPlan.feedback.failed',
    defaultMessage: 'We could not record your feedback. Please try again.',
  },
});

/**
 * Routes clinician/health-coach feedback on the Zori Pulse action plan (the
 * coach narrative and individual action items) to the agents action-plan
 * feedback endpoint, tied to the coach run that produced the plan.
 *
 * Reads `subject_id` from the executive-summary context and `project_id` from
 * the active project so callers only supply the coach `sourceRunId` and the
 * thumb. Gated on the `zoriPulseScan` feature via {@link zoriPulseEnabled};
 * callers should hide the affordance when it is off.
 */
export const useActionPlanFeedback = (): UseActionPlanFeedback => {
  const { formatMessage } = useIntl();
  const [notify] = useNotify();
  const mixpanel = useMixpanel();
  const apiClient = useApiClient();
  const hasFeature = useFeatures();
  const projectId = useActiveProjectId();
  const { patient } = useExecutiveSummaryData();
  const subjectId = patient?.id;

  const zoriPulseEnabled = hasFeature('zoriPulseScan');

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const submitFeedback = React.useCallback(
    async (args: SubmitActionPlanFeedbackArgs): Promise<boolean> => {
      if (!zoriPulseEnabled || !subjectId || !projectId || !args.sourceRunId) {
        notify(formatMessage(messages.failed), 'error');
        return false;
      }

      setIsSubmitting(true);
      const trimmedComment = args.comment?.trim();
      let didScore = false;
      try {
        const response = await apiClient.request(
          'POST /v1/agents-v2/zori-pulse/action-plan/feedback',
          {
            subject_id: subjectId,
            project_id: projectId,
            source_run_id: args.sourceRunId,
            target: args.target,
            sentiment: args.sentiment,
            ...(args.actionItemTitle
              ? { action_item_title: args.actionItemTitle }
              : {}),
            ...(trimmedComment ? { comment: trimmedComment } : {}),
          },
        );
        didScore = response.data.scored;
        notify(formatMessage(messages.submitted), 'success');
      } catch (err) {
        console.error(err);
        notify(formatMessage(messages.failed), 'error');
      } finally {
        setIsSubmitting(false);
        mixpanel.track('action_plan_feedback_submitted', {
          target: args.target,
          sentiment: args.sentiment,
          didScore,
        });
      }
      return didScore;
    },
    [
      apiClient,
      formatMessage,
      mixpanel,
      notify,
      projectId,
      subjectId,
      zoriPulseEnabled,
    ],
  );

  return { zoriPulseEnabled, isSubmitting, submitFeedback };
};

export default useActionPlanFeedback;
